/**
 * API 호출 함수들을 중앙화한 서비스 모듈
 */

import logger from '../utils/logger';

// API 베이스 URL - .env 파일 우선 사용, 없으면 빈 문자열
export const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const API_BASE_URL = `${API_BASE}/api`; // 로그 출력 및 하위 호환성용

logger.info('🌐 API 설정:', {
  API_BASE_URL,
  API_BASE,
  환경: import.meta.env.MODE
})

// 인증 헤더 가져오기
function getAuthHeaders() {
  const token = localStorage.getItem('access_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// TTS 생성 API
export async function generateTTS(text, voiceType) {
  logger.debug(`🔊 TTS API 호출 - Text: "${text?.substring(0, 30)}..."`)
  
  // 텍스트 해시 생성 (디버깅용) - 유니코드 안전 방식
  const encoder = new TextEncoder()
  const data = encoder.encode(text.substring(0, 20))
  const hashArray = Array.from(data.slice(0, 8))
  const textHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  logger.debug(`🔑 텍스트 해시: ${textHash}`)
  
  const response = await fetch(`${API_BASE}/api/tts/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      text: text,
      voice_type: voiceType
    })
  })

  if (!response.ok) {
    throw new Error('TTS 생성 실패')
  }

  const audioBlob = await response.blob()
  
  // Blob 복사본 생성 (원본 보존)
  const audioBlobCopy = new Blob([audioBlob], { type: audioBlob.type })
  const audioUrl = URL.createObjectURL(audioBlobCopy)
  
  logger.debug(`✅ TTS Blob URL 생성 - Size: ${audioBlob.size} bytes, URL: ${audioUrl}, Hash: ${textHash}`)
  return audioUrl
}

// 프로젝트 기반 미디어 업로드 (이미지, 비디오, GIF)
export async function uploadProjectMedia(projectId, sectionId, file, projectName = null) {
  const fileName = file.name.toLowerCase()
  let mediaType = 'image'
  
  // 파일 타입 결정
  if (file.type.startsWith('video/') || fileName.match(/\.(mp4|mov|avi|webm)$/i)) {
    mediaType = 'video'
  } else if (fileName.endsWith('.gif')) {
    mediaType = 'gif'
  }
  
  logger.debug(`📁 프로젝트 미디어 업로드 - Type: ${mediaType}, Project: ${projectId}, Section: ${sectionId}`)
  
  try {
    const formData = new FormData()
    formData.append('project_id', projectId)
    formData.append('section_id', sectionId)
    formData.append('file', file, file.name)
    formData.append('media_type', mediaType)
    formData.append('title', `${mediaType}_Section_${sectionId}`)
    
    // 프로젝트명 전달 (저장 경로 구성용)
    if (projectName) {
      formData.append('project_name', projectName)
    }
    
    const response = await fetch(`${API_BASE}/api/project-video/upload-media`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
      },
      body: formData
    })
    
    if (!response.ok) {
      const errorText = await response.text()
      logger.error(`미디어 업로드 실패 (${response.status}): ${errorText}`)
      throw new Error('미디어 업로드 실패')
    }
    
    const data = await response.json()
    logger.info(`✅ ${mediaType} Supabase 업로드 성공:`, data.media_url)
    
    // Supabase Storage URL 반환
    return data.media_url || data.url
  } catch (error) {
    logger.error('미디어 업로드 실패:', error)
    // 실패 시 null 반환 (로컬 파일 계속 사용)
    return null
  }
}

// 이미지 업로드 (별칭 - 기존 코드 호환성)
export async function uploadProjectImage(projectId, sectionId, imageFile) {
  return uploadProjectMedia(projectId, sectionId, imageFile)
}

// 프로젝트 기반 TTS 생성 (백엔드 API - FormData)
export async function generateProjectTTS(projectId, sectionId, text, voiceSettings) {
  logger.debug(`🎯 프로젝트 TTS 생성 - Project: ${projectId}, Section: ${sectionId}`)
  
  try {
    // FormData 생성
    const formData = new FormData()
    formData.append('project_id', projectId)
    formData.append('text', text)
    formData.append('voice_type', voiceSettings?.voice || 'female')
    formData.append('title', `TTS_Section_${sectionId}`)
    
    const response = await fetch(`${API_BASE}/api/project-video/generate-tts`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
        // Content-Type 제거 (FormData는 자동 설정)
      },
      body: formData
    })

    if (!response.ok) {
      // 실패 시 기존 API로 폴백
      logger.warn('프로젝트 TTS API 실패, 기본 TTS API로 폴백')
      return await generateTTS(text, voiceSettings?.voice || 'female')
    }

    const data = await response.json()
    
    // URL 또는 Blob 처리
    if (data.audio_url) {
      return data.audio_url
    } else if (data.audio_blob) {
      const audioBlob = await fetch(data.audio_blob).then(r => r.blob())
      return URL.createObjectURL(audioBlob)
    }
    
    throw new Error('TTS 응답에 오디오 데이터가 없습니다')
  } catch (error) {
    logger.error('프로젝트 TTS 생성 실패:', error)
    // 폴백: 기존 TTS API 사용
    return await generateTTS(text, voiceSettings?.voice || 'female')
  }
}

// 프로젝트 전체 TTS 생성 (백엔드 API)
export async function generateAllProjectTTS(projectId, sections, voiceSettings) {
  logger.debug(`🎯 프로젝트 전체 TTS 생성 - Project: ${projectId}, Sections: ${sections.length}개`)
  
  try {
    // FormData 생성
    const formData = new FormData()
    formData.append('project_id', projectId)
    formData.append('voice_type', voiceSettings?.voice || 'female')
    
    // 섹션 데이터 추가 (텍스트가 있는 섹션만)
    const sectionsData = sections
      .filter(section => section.text && section.text.trim())
      .map(section => ({
        id: section.id,
        text: section.text,
        index: section.index
      }))
    
    formData.append('sections', JSON.stringify(sectionsData))
    
    const response = await fetch(`${API_BASE}/api/project-video/generate-all-tts`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
      },
      body: formData
    })

    const data = await response.json()
    logger.debug('백엔드 전체 TTS 응답:', data)
    
    if (!response.ok) {
      const errorText = JSON.stringify(data)
      logger.warn(`전체 TTS API 실패 (${response.status}): ${errorText}`)
      throw new Error(`전체 TTS 생성 실패: ${errorText}`)
    }

    // 성공 응답 처리 - 다양한 응답 형식 지원
    if (data.success && data.results) {
      logger.debug('✅ 전체 TTS 생성 완료:', data.results)
      return data.results  // { section_id: audio_url, ... } 형태
    } else if (data.results) {
      // success 필드 없이 results만 있는 경우
      logger.debug('✅ 전체 TTS 생성 완료 (단순 형식):', data.results)
      return data.results
    } else if (Object.keys(data).length > 0 && !data.error && !data.detail) {
      // 직접적으로 섹션 ID -> URL 매핑인 경우
      logger.debug('✅ 전체 TTS 생성 완료 (직접 매핑):', data)
      return data
    }
    
    throw new Error('전체 TTS 응답에 결과 데이터가 없습니다')
  } catch (error) {
    logger.error('프로젝트 전체 TTS 생성 실패:', error)
    throw error
  }
}

// 프로젝트 자막 생성 (백엔드 API)
export async function generateProjectSubtitle(projectId, sectionId, audioUrl) {
  logger.debug(`🎯 프로젝트 자막 생성 - Project: ${projectId}, Section: ${sectionId}`)
  
  try {
    const response = await fetch(`${API_BASE}/api/project-video/generate-subtitle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        project_id: projectId,
        section_id: sectionId,
        audio_url: audioUrl
      })
    })

    if (!response.ok) {
      throw new Error('자막 생성 실패')
    }

    const data = await response.json()
    return data.subtitles || []
  } catch (error) {
    logger.error('프로젝트 자막 생성 실패:', error)
    throw error
  }
}

// 프로젝트 비디오 변환 (백엔드 API - FormData)
export async function convertProjectVideo(projectId, sectionId, imageFile, text, voiceType, subtitleSettings, videoSettings, titleSettings) {
  logger.debug(`🎯 프로젝트 비디오 변환 - Project: ${projectId}, Section: ${sectionId}`)
  
  try {
    // FormData 생성
    const formData = new FormData()
    formData.append('project_id', projectId)
    formData.append('file', imageFile) // 이미지 파일
    formData.append('text', text)
    formData.append('voice_type', voiceType || 'female')
    formData.append('output_title', `Video_Section_${sectionId}`)
    
    // 설정들은 JSON 문자열로 전달
    if (subtitleSettings) {
      formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
    }
    if (videoSettings) {
      formData.append('video_settings', JSON.stringify(videoSettings))
    }
    if (titleSettings) {
      formData.append('title_settings', JSON.stringify(titleSettings))
    }
    
    const response = await fetch(`${API_BASE}/api/project-video/convert-with-tts-direct`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
        // Content-Type 제거 (FormData는 자동 설정)
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error('비디오 변환 실패')
    }

    const data = await response.json()
    return data.video_url || data.video_blob
  } catch (error) {
    logger.error('프로젝트 비디오 변환 실패:', error)
    throw error
  }
}

// 전체 워크플로우 실행 (백엔드 API - FormData)
export async function completeProjectWorkflow(projectId, imageFile, text, voiceType, subtitleSettings) {
  logger.debug(`🎯 전체 워크플로우 실행 - Project: ${projectId}`)
  
  try {
    // FormData 생성
    const formData = new FormData()
    formData.append('project_id', projectId)
    
    // 이미지와 텍스트가 있는 경우만 추가
    if (imageFile) {
      formData.append('file', imageFile)
    }
    if (text) {
      formData.append('text', text)
    }
    formData.append('voice_type', voiceType || 'female')
    
    if (subtitleSettings) {
      formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
    }
    
    const response = await fetch(`${API_BASE}/api/project-video/complete-workflow`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
        // Content-Type 제거 (FormData는 자동 설정)
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error('워크플로우 실행 실패')
    }

    const data = await response.json()
    return data
  } catch (error) {
    logger.error('전체 워크플로우 실행 실패:', error)
    throw error
  }
}

// 이미지를 MP4로 변환 API
export async function convertImageToMp4(file, duration = 5.0, subtitleText = '', subtitleSettings = null, videoSettings = null, titleSettings = null) {
  logger.debug('🎬 convertImageToMp4 호출됨')
  logger.debug('📁 파일 정보:', {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified
  })
  
  const formData = new FormData()
  formData.append('file', file)
  formData.append('duration', duration.toString())
  
  if (subtitleText) {
    logger.debug('📝 API로 전송하는 한글 텍스트:', subtitleText)
    formData.append('subtitle_text', subtitleText)
  }
  
  if (subtitleSettings) {
    formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
  }
  
  if (videoSettings) {
    logger.debug('📺 API로 전송하는 비디오 설정:', videoSettings)
    formData.append('video_settings', JSON.stringify(videoSettings))
  }
  
  if (titleSettings && titleSettings.enabled && titleSettings.text) {
    logger.debug('📝 API로 전송하는 제목 설정:', titleSettings)
    formData.append('title_settings', JSON.stringify(titleSettings))
  }

  const response = await fetch(`${API_BASE}/api/video/convert`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    const errorText = await response.text()
    logger.error('❌ MP4 변환 실패:', response.status, errorText)
    throw new Error(`MP4 변환 실패: ${response.status} - ${errorText}`)
  }

  const mp4Blob = await response.blob()
  return URL.createObjectURL(mp4Blob)
}

// TTS 포함 비디오 생성 API
export async function generateVideoWithTTS(file, sectionText, voiceType, subtitleSettings, videoSettings = null, titleSettings = null) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('text', sectionText)  // 백엔드가 'text' 필드를 기대함
  formData.append('voice_type', voiceType)
  formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
  
  if (videoSettings) {
    logger.debug('📺 TTS 비디오에 전송하는 비디오 설정:', videoSettings)
    formData.append('video_settings', JSON.stringify(videoSettings))
  }
  
  if (titleSettings && titleSettings.enabled && titleSettings.text) {
    logger.debug('📝 TTS 비디오에 전송하는 제목 설정:', titleSettings)
    formData.append('title_settings', JSON.stringify(titleSettings))
  }

  const response = await fetch(`${API_BASE}/api/video/convert-with-tts`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    throw new Error('TTS 포함 비디오 생성 실패')
  }

  const mp4Blob = await response.blob()
  return URL.createObjectURL(mp4Blob)
}

// 업로드된 오디오와 비디오 생성 API
export async function generateVideoWithUploadedAudio(file, audioFile, subtitleText, subtitleSettings, videoSettings = null, titleSettings = null) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('audio_file', audioFile)
  
  if (subtitleText) {
    logger.debug('📝 오디오 비디오에 전송하는 자막 텍스트:', subtitleText)
    formData.append('subtitle_text', subtitleText)
  }
  
  formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
  
  if (videoSettings) {
    logger.debug('📺 오디오 비디오에 전송하는 비디오 설정:', videoSettings)
    formData.append('video_settings', JSON.stringify(videoSettings))
  }
  
  if (titleSettings && titleSettings.enabled && titleSettings.text) {
    logger.debug('📝 오디오 비디오에 전송하는 제목 설정:', titleSettings)
    formData.append('title_settings', JSON.stringify(titleSettings))
  }

  const response = await fetch(`${API_BASE}/api/video/convert-with-audio`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    throw new Error('오디오 포함 비디오 생성 실패')
  }

  const mp4Blob = await response.blob()
  return URL.createObjectURL(mp4Blob)
}

// 기존 비디오에 자막 추가 API
export async function addSubtitleToVideo(videoFile, subtitleText, subtitleSettings, titleSettings = null) {
  logger.debug('📝 비디오에 자막 추가 API 호출')
  
  const formData = new FormData()
  
  // videoFile이 Blob URL인 경우 실제 Blob으로 변환
  if (typeof videoFile === 'string' && videoFile.startsWith('blob:')) {
    const response = await fetch(videoFile)
    const blob = await response.blob()
    formData.append('file', blob, 'video.mp4')
  } else {
    formData.append('file', videoFile)
  }
  
  if (subtitleText) {
    logger.debug('📝 비디오에 추가할 자막 텍스트:', subtitleText)
    formData.append('subtitle_text', subtitleText)
  }
  
  if (subtitleSettings) {
    formData.append('subtitle_settings', JSON.stringify(subtitleSettings))
  }
  
  if (titleSettings && titleSettings.enabled && titleSettings.text) {
    logger.debug('📝 비디오에 추가할 제목 설정:', titleSettings)
    formData.append('title_settings', JSON.stringify(titleSettings))
  }

  const response = await fetch(`${API_BASE}/api/video/add-subtitle`, {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    const errorText = await response.text()
    logger.error('❌ 자막 추가 실패:', response.status, errorText)
    throw new Error(`자막 추가 실패: ${response.status} - ${errorText}`)
  }

  const mp4Blob = await response.blob()
  return URL.createObjectURL(mp4Blob)
}

// 영상들 이어붙이기 API
export async function concatenateVideos(videoUrls) {
  logger.debug('🔗 영상 이어붙이기 API 호출:', videoUrls)
  
  // Blob URL들을 실제 파일 데이터로 변환
  const formData = new FormData()
  
  for (let i = 0; i < videoUrls.length; i++) {
    const videoUrl = videoUrls[i]
    
    try {
      // Blob URL을 실제 Blob으로 변환
      const response = await fetch(videoUrl)
      const blob = await response.blob()
      
      // FormData에 파일로 추가
      formData.append('video_files', blob, `video_${i}.mp4`)
      logger.debug(`📁 영상 ${i+1}/${videoUrls.length} 파일 준비 완료`)
      
    } catch (error) {
      logger.error(`❌ 영상 ${i+1} 변환 실패:`, error)
      throw new Error(`영상 ${i+1} 처리 중 오류가 발생했습니다.`)
    }
  }

  logger.debug('🚀 서버로 전송 중...')
  const response = await fetch(`${API_BASE}/api/video/concatenate`, {
    method: 'POST',
    body: formData  // JSON 대신 FormData 사용
  })

  if (!response.ok) {
    const errorText = await response.text()
    logger.error('❌ 서버 응답 오류:', errorText)
    throw new Error(`영상 이어붙이기 실패: ${response.status} ${response.statusText}`)
  }
  
  logger.debug('✅ 서버 응답 성공')

  const finalVideoBlob = await response.blob()
  return finalVideoBlob  // Blob 객체를 직접 반환
}

// 프로젝트 기반 영상 합치기 (백엔드 API)
export async function concatenateProjectVideos(projectId, sections, settings, onProgress) {
  logger.debug(`🎯 프로젝트 영상 합치기 - Project: ${projectId}, Sections: ${sections.length}개`)
  
  // 섹션 ID가 Date.now() 형태인지 확인
  logger.warn('⚠️ 섹션 ID 형식 확인:')
  sections.forEach((s, idx) => {
    const isLocalId = typeof s.id === 'number' && s.id > 1000000000000 // Date.now() 형태
    logger.info(`섹션 ${idx}:`, {
      id: s.id,
      idType: typeof s.id,
      isLocalId: isLocalId,
      isUUID: typeof s.id === 'string' && s.id.includes('-'),
      index: s.index,
      text: s.text?.substring(0, 50),
      hasImageFile: s.imageFile instanceof File,
      imageFileName: s.imageFile?.name,
      hasVideoFile: s.mp4File instanceof File || s.videoFile instanceof File,
      hasAudioFile: s.audioFile instanceof File,
      audioFileName: s.audioFile?.name,
      audioFileType: s.audioFile?.type,
      audioFileSize: s.audioFile?.size,
      hasAudioString: typeof s.audioFile === 'string'
    })
  })
  
  try {
    // FormData 생성
    const formData = new FormData()
    formData.append('project_id', projectId)
    
    // 백엔드 호환성을 위해 section_ids도 전달
    const sectionIdList = sections.map(section => section.id)
    formData.append('section_ids', JSON.stringify(sectionIdList))
    
    // 섹션 메타데이터 준비 (File 객체 제외)
    const sectionsMetadata = sections.map((section, index) => {
      // File 객체들을 특별히 처리
      const metadata = {
        id: section.id,
        index: section.index || index,
        text: section.text,
        // 자막 설정
        fontSize: section.fontSize,
        fontFamily: section.fontFamily,
        textColor: section.textColor,
        strokeColor: section.strokeColor,
        strokeWidth: section.strokeWidth,
        textPosition: section.textPosition,
        duration: section.duration
      }
      
      // URL 필드 추가 (문자열인 경우만)
      if (typeof section.imageUrl === 'string') {
        metadata.imageUrl = section.imageUrl
      }
      if (typeof section.videoUrl === 'string') {
        metadata.videoUrl = section.videoUrl  
      }
      if (typeof section.audioFile === 'string') {
        metadata.audioUrl = section.audioFile
      }
      
      // File 객체가 있는지 표시
      if (section.imageFile instanceof File || section.imageFile instanceof Blob) {
        metadata.hasImageFile = true
      }
      if (section.mp4File instanceof File || section.mp4File instanceof Blob || 
          section.videoFile instanceof File || section.videoFile instanceof Blob) {
        metadata.hasVideoFile = true
        // mp4File에 오디오가 포함되어 있을 수 있음
        if (section.hasAudioInVideo) {
          metadata.hasAudioInVideo = true
        }
      }
      if (section.audioFile instanceof File || section.audioFile instanceof Blob) {
        metadata.hasAudioFile = true
      }
      
      return metadata
    })
    
    // 백엔드가 기대하는 'sections' 필드로 전송
    formData.append('sections', JSON.stringify(sectionsMetadata))
    
    // section_ids는 이미 위에서 추가했으므로 제거
    
    // File 객체들은 FormData에 직접 추가
    sections.forEach((section, index) => {
      // 이미지 파일
      if (section.imageFile instanceof File || section.imageFile instanceof Blob) {
        logger.debug(`섹션 ${index}: 이미지 파일 추가`, section.imageFile.name || 'blob')
        formData.append(`image_${index}`, section.imageFile, `image_${section.id}.png`)
      }
      // 비디오 파일
      if (section.mp4File instanceof File || section.mp4File instanceof Blob) {
        logger.info(`섹션 ${index}: MP4 파일 추가 (오디오 포함: ${section.hasAudioInVideo})`, section.mp4File.name || 'blob')
        formData.append(`video_${index}`, section.mp4File, `video_${section.id}.mp4`)
      } else if (section.videoFile instanceof File || section.videoFile instanceof Blob) {
        logger.debug(`섹션 ${index}: 비디오 파일 추가`, section.videoFile.name || 'blob')
        formData.append(`video_${index}`, section.videoFile, `video_${section.id}.mp4`)
      }
      // 오디오 파일
      if (section.audioFile instanceof File || section.audioFile instanceof Blob) {
        logger.debug(`섹션 ${index}: 오디오 파일 추가`, section.audioFile.name || 'blob')
        formData.append(`audio_${index}`, section.audioFile, `audio_${section.id}.mp3`)
      }
    })
    
    // 설정 전달
    if (settings) {
      formData.append('subtitle_settings', JSON.stringify(settings.subtitleSettings || {}))
      
      // 비디오 설정에 9:16 비율 강제
      const videoSettings = {
        ...settings.videoSettings,
        aspectRatio: '9:16',
        width: 1080,
        height: 1920,
        fitMode: 'contain', // 비율 유지하면서 중앙 배치
        backgroundColor: '#000000', // 검은 배경
        position: 'center' // 중앙 정렬
      }
      logger.info('비디오 설정:', videoSettings)
      formData.append('video_settings', JSON.stringify(videoSettings))
      
      formData.append('title_settings', JSON.stringify(settings.titleSettings || {}))
    }
    
    formData.append('output_title', `Merged_Video_${Date.now()}`)
    
    const url = `${API_BASE}/api/project-video/merge-sections`
    logger.info('🚀 서버로 요청 전송 시작...')
    logger.info(`📍 요청 URL: ${url}`)
    logger.info('📦 요청 메서드: POST')
    
    // 진행률 콜백 호출
    if (onProgress) {
      onProgress({ progress: 10, message: '서버에 요청 전송 중...' })
    }
    
    // 서버 요청 시간 추적
    const startTime = Date.now()
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...getAuthHeaders()
      },
      body: formData
    })
    
    // 서버 응답 시간 계산
    const responseTime = Date.now() - startTime
    logger.info(`📡 서버 응답 수신: ${response.status} ${response.statusText} (소요시간: ${responseTime}ms)`)
    logger.info(`Content-Type: ${response.headers.get('content-type')}`)
    
    if (onProgress) {
      onProgress({ progress: 50, message: '서버에서 영상 처리 중...' })
    }
    
    if (!response.ok) {
      // 에러 상세 정보 확인
      const errorText = await response.text()
      logger.warn(`프로젝트 영상 합치기 실패 (${response.status}): ${errorText}`)
      
      // JWT 토큰 만료 체크
      if (response.status === 401 || errorText.includes('token is expired') || errorText.includes('invalid JWT')) {
        logger.error('인증 토큰이 만료되었습니다. 다시 로그인해주세요.')
        // 로그인 페이지로 리다이렉트
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        window.location.href = '/login'
        throw new Error('인증이 만료되었습니다. 다시 로그인해주세요.')
      }
      
      logger.warn('로컬 방식으로 폴백')
      throw new Error(`백엔드 영상 합치기 실패: ${errorText}`)
    }
    
    // 응답이 JSON인지 Blob인지 확인
    const contentType = response.headers.get('content-type')
    
    if (onProgress) {
      onProgress({ progress: 80, message: '영상 다운로드 중...' })
    }
    
    let result
    if (contentType?.includes('application/json')) {
      const data = await response.json()
      
      // URL 또는 Blob 반환
      if (data.video_url) {
        result = data.video_url
      } else if (data.video_blob) {
        const blob = await fetch(data.video_blob).then(r => r.blob())
        result = blob
      } else {
        throw new Error('영상 합치기 응답에 비디오 데이터가 없습니다')
      }
    } else {
      // 직접 Blob으로 반환
      const blob = await response.blob()
      result = blob
    }
    
    if (onProgress) {
      onProgress({ progress: 100, message: '완료!' })
    }
    
    return result
  } catch (error) {
    logger.error('프로젝트 영상 합치기 실패:', error)
    throw error
  }
}